// client/src/lib/locationEnrichment.ts
//
// Genuinely local inputs for location pages (#459). The regional living-cost
// index has 5 distinct values across 360 places, so it cannot make a page
// local. These fields can, and each carries its own source and date.
//
// Data lives in src/data/location-enrichment.json, keyed by location slug. It
// is empty until the phase-one places are filled from the official sources in
// ENRICHMENT_FIELDS. Never estimate a value: a place is either fully enriched
// from published data or not enriched at all.

import enrichmentData from '@/data/location-enrichment.json';

export interface Sourced<T> {
  value: T;
  /** Publisher and dataset, e.g. "ONS, Annual Survey of Hours and Earnings 2025". */
  source: string;
  sourceUrl: string;
  /** ISO date of the reference period or publication the value comes from. */
  asOf: string;
}

export interface LocationEnrichment {
  /** Median gross annual earnings, residence-based. */
  medianEarnings: Sourced<number>;
  /** Period life expectancy at 65, in years. */
  lifeExpectancyAt65: Sourced<{ men: number; women: number }>;
  /** Band D council tax a year (NI: domestic rates on a typical property). */
  councilTaxBandD: Sourced<number>;
  /** Median private rent a month. */
  medianMonthlyRent: Sourced<number>;
  /** The Local Government Pension Scheme fund council and school staff in the area belong to. */
  lgpsFund: Sourced<{ name: string; url: string }>;
}

export const ENRICHMENT_FIELDS: Record<keyof LocationEnrichment, string> = {
  medianEarnings: 'ONS ASHE table 8 (residence), by local authority; Scotland and NI via ONS ASHE as well',
  lifeExpectancyAt65: 'ONS (England and Wales), NRS (Scotland), NISRA (Northern Ireland), by local authority',
  councilTaxBandD: 'GOV.UK (England), Welsh Government, Scottish Government; LPS NI domestic rates',
  medianMonthlyRent: 'ONS Price Index of Private Rents, by local authority',
  lgpsFund: 'LGPS administering authorities (England and Wales), SPPA list (Scotland), NILGOSC (Northern Ireland)',
};

/** Phase one (#459): the 50 places with ~80% of 90-day location impressions. */
export const PHASE_ONE_SLUGS = [
  'exeter', 'leeds', 'east-devon', 'cambridge', 'dumfries-and-galloway', 'stockton-on-tees',
  'city-of-edinburgh', 'telford-and-wrekin', 'city-of-london', 'city-of-nottingham', 'liverpool',
  'mid-devon', 'wolverhampton', 'blackpool', 'burnley', 'belfast', 'watford',
  'cheshire-west-and-chester', 'city-of-glasgow', 'middlesbrough', 'norwich', 'east-ayrshire',
  'denbighshire', 'dorset', 'city-of-dundee', 'ealing', 'nuneaton-and-bedworth', 'brighton-and-hove',
  'cheltenham', 'armagh-city-banbridge-and-craigavon', 'shropshire', 'north-ayrshire', 'south-ayrshire',
  'east-hertfordshire', 'antrim-and-newtownabbey', 'newcastle-upon-tyne', 'manchester', 'flintshire',
  'bournemouth-christchurch-and-poole', 'harrow', 'kings-lynn-and-west-norfolk', 'neath-port-talbot',
  'fermanagh-and-omagh', 'gwynedd', 'city-of-derby', 'wrexham', 'blackburn-with-darwen',
  'city-of-bristol', 'richmond-upon-thames', 'east-hampshire',
] as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isSourced(field: unknown): field is Sourced<unknown> {
  if (!field || typeof field !== 'object') return false;
  const f = field as Record<string, unknown>;
  return f.value !== undefined && f.value !== null
    && typeof f.source === 'string' && f.source.length > 0
    && typeof f.sourceUrl === 'string' && /^https:\/\//.test(f.sourceUrl)
    && typeof f.asOf === 'string' && ISO_DATE.test(f.asOf);
}

/** Fields that are missing or lack a value, source, URL or date. Empty means fully enriched. */
export function missingEnrichmentFields(record: unknown): (keyof LocationEnrichment)[] {
  const r = (record ?? {}) as Record<string, unknown>;
  return (Object.keys(ENRICHMENT_FIELDS) as (keyof LocationEnrichment)[]).filter(key => !isSourced(r[key]));
}

/**
 * Load and validate the enrichment file. A record that is present but
 * incomplete is a data error, not "unenriched", so it throws: a half-filled
 * place must not silently fall back to the regional figures.
 */
export function loadEnrichment(raw: { places: Record<string, unknown> }): Record<string, LocationEnrichment> {
  const out: Record<string, LocationEnrichment> = {};
  for (const [slug, record] of Object.entries(raw.places)) {
    const missing = missingEnrichmentFields(record);
    if (missing.length) {
      throw new Error(`location-enrichment.json: ${slug} is missing or unsourced: ${missing.join(', ')}`);
    }
    out[slug] = record as LocationEnrichment;
  }
  return out;
}

const ENRICHMENT = loadEnrichment(enrichmentData as { places: Record<string, unknown> });

export function getEnrichment(slug: string): LocationEnrichment | undefined {
  return ENRICHMENT[slug];
}

export function isEnriched(location: { slug: string }): boolean {
  return Boolean(ENRICHMENT[location.slug]);
}

export function enrichedSlugs(): string[] {
  return Object.keys(ENRICHMENT);
}

// --- The local retirement number -------------------------------------------

export const LOCAL_NUMBER_ASSUMPTIONS = {
  /** Age the pot starts paying out and the State Pension starts. */
  retirementAge: 67,
  /** Real (after-inflation) return on the pot while it is drawn down. */
  realReturn: 0.02,
} as const;

export interface LocalNumberInputs {
  /** Regional living costs excluding housing and council tax (existing dataset). */
  annualLivingCosts: number;
  statePension: number;
  enrichment: LocationEnrichment;
  /** Owners pay no rent in retirement; renters pay the local median. */
  tenure: 'owner' | 'renter';
}

export interface LocalNumberStep {
  label: string;
  amount: number;
  note: string;
}

export interface LocalNumber {
  annualSpending: number;
  annualGap: number;
  yearsInRetirement: number;
  pot: number;
  /** Years of local median salary the pot represents. */
  yearsOfLocalSalary: number;
  steps: LocalNumberStep[];
}

/** Present value of £1 a year for `years` years at `rate`, paid at the start of each year. */
export function annuityFactor(years: number, rate: number): number {
  if (years <= 0) return 0;
  if (rate === 0) return years;
  return ((1 - Math.pow(1 + rate, -years)) / rate) * (1 + rate);
}

/**
 * The pot needed at 67 in this place: local spending (living costs, council
 * tax, rent if renting) less the State Pension, paid for the years a 67-year-old
 * here can expect to live, at a 2% real return. Shows its working.
 */
export function localRetirementNumber({ annualLivingCosts, statePension, enrichment, tenure }: LocalNumberInputs): LocalNumber {
  const { retirementAge, realReturn } = LOCAL_NUMBER_ASSUMPTIONS;
  const rent = tenure === 'renter' ? enrichment.medianMonthlyRent.value * 12 : 0;
  const councilTax = enrichment.councilTaxBandD.value;
  const annualSpending = annualLivingCosts + councilTax + rent;
  const annualGap = Math.max(0, annualSpending - statePension);
  const { men, women } = enrichment.lifeExpectancyAt65.value;
  // Life expectancy is measured at 65; the pot starts at 67.
  const yearsInRetirement = Math.max(0, 65 + (men + women) / 2 - retirementAge);
  const pot = annualGap * annuityFactor(yearsInRetirement, realReturn);
  return {
    annualSpending,
    annualGap,
    yearsInRetirement,
    pot,
    yearsOfLocalSalary: pot / enrichment.medianEarnings.value,
    steps: [
      { label: 'Living costs', amount: annualLivingCosts, note: 'Regional price level, excluding housing and council tax' },
      { label: 'Council tax (Band D)', amount: councilTax, note: enrichment.councilTaxBandD.source },
      ...(tenure === 'renter' ? [{ label: 'Rent', amount: rent, note: enrichment.medianMonthlyRent.source }] : []),
      { label: 'Less full new State Pension', amount: -statePension, note: '2026/27 rate' },
      { label: `Years from ${retirementAge}`, amount: yearsInRetirement, note: enrichment.lifeExpectancyAt65.source },
    ],
  };
}
